import { useState } from "react";
import { FOOTER } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    toast.success("You're on the list.", {
      description: "We'll ping you when Mymech opens to your region.",
    });
    setEmail("");
  };

  const cols = [
    {
      title: "Product",
      links: ["Agents", "Console", "VIN decoder", "Pricing", "Roadmap"],
    },
    {
      title: "Company",
      links: ["Manifesto", "Careers · 4 open", "Press kit", "Contact"],
    },
    {
      title: "Legal",
      links: ["Privacy", "Terms", "Data trust", "Vulnerability disclosure"],
    },
  ];

  return (
    <footer
      data-testid={FOOTER.section}
      className="relative bg-void border-t border-white/10"
    >
      {/* Massive CTA */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 border-b border-white/10">
        <div className="lg:col-span-7">
          <span className="chip-brand">Stay in the loop</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white tracking-tight leading-[1.05]">
            Auto care you can<br />
            <span className="text-mymech-blue">trust</span>, made easier.
          </h2>
        </div>
        <div className="lg:col-span-5 flex flex-col justify-end">
          <p className="text-white/60 text-base font-light leading-relaxed mb-8">
            Join 42,000+ drivers, mechanics, and fleet operators getting weekly
            teardowns, TSB alerts, and early access.
          </p>
          <form onSubmit={submit} className="flex items-center border-b border-white/15 focus-within:border-mclaren transition-colors py-3 gap-3">
            <input
              data-testid={FOOTER.newsletterInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="you@garage.com"
              className="flex-1 bg-transparent outline-none font-mono-tech text-white placeholder:text-white/25 text-base"
            />
            <button
              data-testid={FOOTER.newsletterSubmit}
              type="submit"
              className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-mclaren hover:text-sunset transition-colors"
            >
              Subscribe
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </div>
      </div>

      {/* Link columns */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div>
          <div className="mb-6">
            <BrandLogo tagline className="h-16 max-w-[240px]" />
          </div>
          <p className="text-sm text-mymech-grey leading-relaxed max-w-[260px]">
            A digital auto-care hub connecting vehicle owners with trusted mechanics and garages.
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mb-5">
              {c.title}
            </div>
            <ul className="space-y-3">
              {c.links.map((l) => (
                <li key={l}>
                  <a
                    href="#"
                    className="text-sm text-white/70 hover:text-mclaren transition-colors font-light"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Massive brand watermark */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-10">
        <div className="font-display text-white/[0.04] font-bold tracking-tighter leading-none text-[22vw] sm:text-[18vw] lg:text-[14vw] select-none">
          mymech.
        </div>
      </div>

      <div className="border-t border-white/10 py-6 px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-widest text-white/30 max-w-[1400px] mx-auto w-full">
        <span>© 2026 mymech systems inc · all signals reserved</span>
        <span className="flex items-center gap-3">
          <span className="h-1 w-1 bg-cyan-electric rounded-full animate-pulse-dot" />
          All systems nominal · v0.9.4
        </span>
      </div>
    </footer>
  );
};

export default Footer;
